// Shared, site-wide chrome injected on EVERY Flarecade page.
//
// Two universal elements requested by Adam that must appear on every page:
//   1. A floating, top-center "Download Cloudflare One free" button.
//   2. A link to sasecloudmap.com (the SASE Cloud Map comparison tool built by
//      Cloudflare employees) placed alongside the Cloudflare marketing.
//
// Security: every node is built with createElement + textContent (never
// innerHTML), so no data can inject markup. Both external links are hardcoded
// constants that open in a new tab with rel="noopener noreferrer". The page CSP
// (default-src 'self' without navigate-to) permits ordinary link navigations.
//
// The two external destinations are intentionally the ONLY non-Cloudflare-owned
// and Cloudflare-owned URLs this module references:
//   - https://dash.cloudflare.com/sign-up/teams  (Cloudflare One / Zero Trust
//     free plan sign-up — verified from cloudflare.com/plans/zero-trust-services)
//   - https://sasecloudmap.com  (SASE Cloud Map; a personal project by Cloudflare
//     employees, verified reachable and self-described as not officially endorsed)

import "./site-chrome.css";

/** Cloudflare One / Zero Trust free-plan sign-up (verified 2026-09-14). */
export const CLOUDFLARE_ONE_FREE_URL = "https://dash.cloudflare.com/sign-up/teams";
/** SASE Cloud Map — SASE provider comparison tool (verified 2026-09-14). */
export const SASE_CLOUD_MAP_URL = "https://sasecloudmap.com";

const CHROME_ID = "flr-site-chrome";

function extAnchor(href: string, className: string, text: string): HTMLAnchorElement {
  const a = document.createElement("a");
  a.className = className;
  a.href = href;
  a.target = "_blank";
  a.rel = "noopener noreferrer";
  a.textContent = text;
  return a;
}

/**
 * Inject the floating top-center download button plus the SASE Cloud Map link.
 * Idempotent: calling it more than once is a no-op.
 *
 * If the page already has the homepage floatbar (`#floatbar`), the two links are
 * added INTO that bar instead of floating a second bar on top of it, so they
 * never collide. Every other page gets the fixed top-center floating bar.
 */
export function mountSiteChrome(): void {
  if (document.getElementById(CHROME_ID)) return;

  const floatbar = document.getElementById("floatbar");
  if (floatbar) {
    mountIntoFloatbar(floatbar);
    return;
  }

  const bar = document.createElement("div");
  bar.id = CHROME_ID;
  bar.className = "flr-site-chrome";
  bar.setAttribute("role", "complementary");
  bar.setAttribute("aria-label", "Cloudflare One and SASE Cloud Map");

  // Primary floating CTA: Download Cloudflare One free.
  const download = extAnchor(
    CLOUDFLARE_ONE_FREE_URL,
    "flr-chrome__cta",
    "Download Cloudflare One free",
  );
  download.setAttribute(
    "aria-label",
    "Download Cloudflare One free (opens Cloudflare sign-up in a new tab)",
  );

  // Secondary link: SASE Cloud Map (Cloudflare marketing companion).
  const saseMap = extAnchor(SASE_CLOUD_MAP_URL, "flr-chrome__link", "SASE Cloud Map");
  saseMap.setAttribute(
    "aria-label",
    "Open the SASE Cloud Map provider comparison (opens in a new tab)",
  );

  bar.appendChild(download);
  bar.appendChild(saseMap);

  document.body.appendChild(bar);
  // Reserve space so the fixed bar never covers the page's own top content
  // (e.g. a game page's breadcrumb), which sits directly below.
  document.body.classList.add("has-site-chrome");
}

/**
 * Homepage variant: append the two links into the existing floatbar so there is
 * no second floating bar. Uses the floatbar's own button/link styling classes.
 */
function mountIntoFloatbar(floatbar: HTMLElement): void {
  if (floatbar.querySelector(".fb-cf-one")) return;

  const download = extAnchor(
    CLOUDFLARE_ONE_FREE_URL,
    "fb-cf-one",
    "Download Cloudflare One free",
  );
  download.setAttribute(
    "aria-label",
    "Download Cloudflare One free (opens Cloudflare sign-up in a new tab)",
  );

  const saseMap = extAnchor(SASE_CLOUD_MAP_URL, "fb-sase-map", "SASE Cloud Map");
  saseMap.setAttribute(
    "aria-label",
    "Open the SASE Cloud Map provider comparison (opens in a new tab)",
  );

  // Place them at the end of the bar (after the spacer / calc button).
  floatbar.appendChild(download);
  floatbar.appendChild(saseMap);
}
